import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { useGlobalTheme } from '@hooks/application/useColorTheme';
import { Body5, Body7, Container } from '@ui';
import { FlatButton, TransparentFlatButton, SkeletonButton } from '@ui/buttons';
import { fetchScoreBackfills, UNIVERSE_KINDS, BACKFILL_TERMINAL_STATUSES } from '@services/scoreBackfills';
import { ROUTES } from '@constants/routes';
import { createTimeInstance } from '@utils/time';
import {
  specLabelById,
  formatStatus,
  DEFAULT_MARKET_CAP_VERSION,
  formatMarketCapVersion,
  marketCapVersionOf,
  deriveDisplayStatus,
  formatScope,
  formatUniverseKind,
  formatIndex,
  formatJobWindow,
  formatScored,
  formatCount,
  isLegacyWindow,
  isStampedAtScoringTime,
  LEGACY_WINDOW_LABEL,
  LEGACY_WINDOW_NOTE,
  STAMPED_AT_SCORING_TIME_LABEL,
  STAMPED_AT_SCORING_TIME_NOTE,
} from './format';
import { StatusBadge } from './StatusBadge';

const POLL_DELAY = 5000;
const EM_DASH = '—';

// Spec | Universe | Window | Scope | Status | Progress | Updated
const GRID_COLUMNS =
  'minmax(200px, 2.2fr) minmax(120px, 1.3fr) minmax(170px, 1.7fr) minmax(120px, 1.2fr) minmax(110px, 1fr) minmax(90px, 0.9fr) minmax(100px, 1fr)';

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.div`
  min-width: 940px;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 16px;
  padding: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.border};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 16px;
  align-items: center;
  padding: 12px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: -2px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const ChipWrap = styled.div`
  button {
    padding: 6px 14px;
  }
  ${({ $active }) => (!$active ? 'opacity: 0.5;' : '')}
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 14px;
  min-width: 220px;

  &::placeholder {
    color: ${({ theme }) => theme.themeColors.secondaryText};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }
`;

const universeSummary = (universe) => {
  if (!universe) return EM_DASH;
  if (universe.kind === UNIVERSE_KINDS.WHOLE_CORPUS) return 'Whole corpus';
  if (universe.kind === UNIVERSE_KINDS.INDEX) return `Index: ${formatIndex(universe.value)}`;
  if (universe.kind === UNIVERSE_KINDS.TICKERS) {
    const count = Array.isArray(universe.value) ? universe.value.length : 0;
    return `${formatCount(count)} ticker${count === 1 ? '' : 's'}`;
  }
  return formatUniverseKind(universe.kind);
};

const progressLabel = (job) => {
  if (job.pct != null) return `${Math.round(job.pct)}%`;
  return formatScored(job.completed, job.total);
};

// aria-label on role="button" REPLACES the row's inner text, so this string is the entire row for a
// screen reader. Built as parts for that reason — and clauses are APPENDED, never prepended, because
// the leading "<spec> backfill" is what row lookups match on.
const rowAriaLabel = (job, specLabel, displayStatus) => {
  const parts = [`${specLabel} backfill`, `status ${formatStatus(displayStatus)}`, `progress ${progressLabel(job)}`];
  const marketCapVersion = marketCapVersionOf(job);
  if (marketCapVersion !== DEFAULT_MARKET_CAP_VERSION) parts.push(formatMarketCapVersion(marketCapVersion));
  // Pre-existing gap, closed here because this extraction is the first thing with somewhere to put
  // it: the legacy-window line is visible in the Window cell and, until now, unannounced.
  if (isLegacyWindow(job)) parts.push(LEGACY_WINDOW_LABEL);
  if (isStampedAtScoringTime(job)) parts.push(STAMPED_AT_SCORING_TIME_LABEL);
  return parts.join(', ');
};

const isTerminal = (job) => BACKFILL_TERMINAL_STATUSES.includes(job.status);

const updatedTs = (job) => createTimeInstance(job.updated_at || job.created_at).valueOf();

const STATUS_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'running', label: 'Running' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
  { key: 'canceled', label: 'Canceled' },
];

export const BackfillHistory = ({ specs, onOpenJob }) => {
  const navigate = useNavigate();
  const theme = useGlobalTheme();

  const [jobs, setJobs] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [lastLoadedAt, setLastLoadedAt] = React.useState(null);
  const [nowTs, setNowTs] = React.useState(Date.now());
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [query, setQuery] = React.useState('');

  const load = React.useCallback(async () => {
    setError(null);
    const { jobs: list, error: loadError } = await fetchScoreBackfills();
    if (loadError) {
      setError(loadError);
      // Keep the current list on a transient error so active jobs don't vanish — and so the
      // auto-refresh effect keeps polling instead of early-returning on an emptied list.
      setJobs((prev) => prev || []);
    } else {
      setJobs(list || []);
    }
    setLastLoadedAt(Date.now());
  }, []);

  // Initial load.
  React.useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh while any job is non-terminal; stop once all terminal.
  React.useEffect(() => {
    if (!jobs || jobs.length === 0) return undefined;
    const anyActive = jobs.some((j) => !isTerminal(j));
    if (!anyActive) return undefined;
    const id = setInterval(() => {
      load();
    }, POLL_DELAY);
    return () => clearInterval(id);
  }, [jobs, load]);

  // 1s tick so the "Updated Ns ago" label stays live.
  React.useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Count only truly-running jobs so the header matches the "Running" filter chip (status
  // ==='running'); queued jobs are non-terminal but that chip does not surface them.
  const runningCount = React.useMemo(() => (jobs || []).filter((j) => j.status === 'running').length, [jobs]);

  const updatedLabel = React.useMemo(() => {
    if (lastLoadedAt == null) return null;
    const secondsAgo = Math.max(0, Math.floor((nowTs - lastLoadedAt) / 1000));
    if (secondsAgo < 5) return 'Updated just now';
    // Past a minute the seconds are noise — this only gets here once polling has stopped
    // (all jobs terminal), where the exact second stopped mattering.
    if (secondsAgo >= 60) return `Updated ${Math.floor(secondsAgo / 60)}m ago`;
    return `Updated ${secondsAgo}s ago`;
  }, [lastLoadedAt, nowTs]);

  const visibleJobs = React.useMemo(() => {
    if (!jobs) return [];
    const q = query.trim().toLowerCase();
    return jobs
      .filter((job) => statusFilter === 'all' || job.status === statusFilter)
      .filter((job) => {
        if (!q) return true;
        const spec = specLabelById(specs, job.score_spec_id).toLowerCase();
        const uni = universeSummary(job.universe).toLowerCase();
        // Mirrors the row: a v1 job renders no band line, so it matches no band query. Otherwise
        // "Market cap v1" on every job would make "cap" select the whole table instead of a spec
        // named Capex Intent.
        const version = marketCapVersionOf(job);
        const band = version === DEFAULT_MARKET_CAP_VERSION ? '' : formatMarketCapVersion(version).toLowerCase();
        // Same rule for the stamp line: only a row that shows it can match on it.
        const stamp = isStampedAtScoringTime(job) ? STAMPED_AT_SCORING_TIME_LABEL.toLowerCase() : '';
        return spec.includes(q) || uni.includes(q) || (!!band && band.includes(q)) || (!!stamp && stamp.includes(q));
      })
      .sort((a, b) => {
        const aTerm = isTerminal(a);
        const bTerm = isTerminal(b);
        if (aTerm !== bTerm) return aTerm ? 1 : -1; // non-terminal first
        return updatedTs(b) - updatedTs(a); // most-recent updated first
      });
  }, [jobs, specs, statusFilter, query]);

  if (jobs === null) {
    return <Body5 isLowContrast>Loading history…</Body5>;
  }

  const handleOpen = (job) => onOpenJob(job);

  const handleRowKeyDown = (e, job) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      if (e.key !== 'Enter') e.preventDefault(); // stop Space from scrolling
      onOpenJob(job);
    }
  };

  return (
    <div>
      <Container bottom={12} row spaceBetween verticallyCenter>
        <Container row verticallyCenter>
          <Body5 bold>Your backfills</Body5>
          {runningCount > 0 && (
            <Container left={12}>
              <Body7 isLowContrast>Running ({runningCount})</Body7>
            </Container>
          )}
        </Container>
        <Container row verticallyCenter>
          {updatedLabel && (
            <Container right={12}>
              <Body7 isLowContrast>{updatedLabel}</Body7>
            </Container>
          )}
          <TransparentFlatButton onClick={load}>Refresh</TransparentFlatButton>
        </Container>
      </Container>

      {error && (
        <Container bottom={12}>
          <Body5 color={theme.themeColors.error}>{error}</Body5>
        </Container>
      )}

      {jobs.length === 0 ? (
        <Container top={8} column>
          <Body5 isLowContrast>No backfills yet. Choose a spec and start one.</Body5>
          <Container top={12}>
            <FlatButton onClick={() => navigate(ROUTES.scoreBackfill)}>Start a backfill</FlatButton>
          </Container>
        </Container>
      ) : (
        <>
          <Container bottom={12}>
            <FilterBar>
              {STATUS_CHIPS.map((chip) => (
                <ChipWrap key={chip.key} $active={statusFilter === chip.key}>
                  <SkeletonButton
                    onClick={() => setStatusFilter(chip.key)}
                    buttonProps={{
                      'aria-pressed': statusFilter === chip.key,
                      'aria-label': `Filter by ${chip.label}`,
                    }}
                  >
                    {chip.label}
                  </SkeletonButton>
                </ChipWrap>
              ))}
              <SearchInput
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search backfills…"
                aria-label="Search backfills"
              />
            </FilterBar>
          </Container>

          <TableScroll>
            <Table>
              <HeaderRow>
                <Body7 isLowContrast>Spec</Body7>
                <Body7 isLowContrast>Universe</Body7>
                <Body7 isLowContrast>Window</Body7>
                <Body7 isLowContrast>Scope</Body7>
                <Body7 isLowContrast>Status</Body7>
                <Body7 isLowContrast>Progress</Body7>
                <Body7 isLowContrast>Updated</Body7>
              </HeaderRow>

              {visibleJobs.length === 0 && (
                <Container top={16} bottom={8}>
                  <Body5 isLowContrast>No backfills match your filters.</Body5>
                </Container>
              )}

              {visibleJobs.map((job) => {
                const label = specLabelById(specs, job.score_spec_id);
                const updated = createTimeInstance(job.updated_at || job.created_at);
                // A cancel-requested job is still `running` on the wire — filter on job.status
                // (so it stays under the Running chip) but render the derived status.
                const displayStatus = deriveDisplayStatus(job);
                return (
                  <Row
                    key={job.job_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleOpen(job)}
                    onKeyDown={(e) => handleRowKeyDown(e, job)}
                    aria-label={rowAriaLabel(job, label, displayStatus)}
                  >
                    <div>
                      <Body7 isLowContrast>#{job.job_id}</Body7>
                      <Container top={4}>
                        <Body5 bold>{label}</Body5>
                      </Container>
                    </div>
                    <div>
                      <Body7 isLowContrast>{universeSummary(job.universe)}</Body7>
                      {/* The band is a property of the universe — it decides which companies are in
                          scope — so it rides under Universe as a second line, the shape the Window
                          cell already uses for LEGACY_WINDOW_LABEL. Rendered only for a non-default
                          version: a column of identical "Market cap v1" is a line of vertical rhythm
                          per row that teaches nothing, and unlabelled unambiguously means v1. */}
                      {marketCapVersionOf(job) !== DEFAULT_MARKET_CAP_VERSION && (
                        <Container top={4}>
                          <Body7 isLowContrast>{formatMarketCapVersion(marketCapVersionOf(job))}</Body7>
                        </Container>
                      )}
                    </div>
                    <div>
                      <Body7 isLowContrast>{formatJobWindow(job)}</Body7>
                      {/* A job from before the knowable-at switch: the same two dates bounded a
                          different column. Two lines, like the Spec cell, rather than a title alone
                          — a hover-only note is no note on a touch screen. */}
                      {isLegacyWindow(job) && (
                        <Container top={4}>
                          <Body7 isLowContrast title={LEGACY_WINDOW_NOTE}>
                            {LEGACY_WINDOW_LABEL}
                          </Body7>
                        </Container>
                      )}
                      {/* A stamped job's window ended when it was requested, so the end date above is
                          the request date; this line says why, in the legacy marker's shape. */}
                      {isStampedAtScoringTime(job) && (
                        <Container top={4}>
                          <Body7 isLowContrast title={STAMPED_AT_SCORING_TIME_NOTE}>
                            {STAMPED_AT_SCORING_TIME_LABEL}
                          </Body7>
                        </Container>
                      )}
                    </div>
                    <div>
                      <Body7 isLowContrast>{formatScope(job.scope)}</Body7>
                    </div>
                    <div>
                      <StatusBadge status={displayStatus} />
                    </div>
                    <div>
                      <Body7 isLowContrast>{progressLabel(job)}</Body7>
                    </div>
                    <div>
                      <Body7 isLowContrast title={updated.format('MMM D, YYYY h:mm A')}>
                        {updated.fromNow()}
                      </Body7>
                    </div>
                  </Row>
                );
              })}
            </Table>
          </TableScroll>
        </>
      )}
    </div>
  );
};
